import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Avatar } from '@kula-ai/components';
import { KText } from '@/ui-library/components/text';
import { UNDERWAY_POPUP_STRINGS } from './underwayPopup.strings';
import type { UnderwayActivity } from './types';
import { getCandidateApplicationUrl } from './candidateApplicationUrl';
import { BULK_OPERATION_QUEUE_Z_INDEX } from '@/components/ats/BulkOperationQueue';

const STR = UNDERWAY_POPUP_STRINGS.candidatesModal;

// Header cells (opaque bg + borders). Only the candidate list (tbody) scrolls —
// the header row is a fixed block above it (see the <Thead>/<Tbody> below), so
// the header can't be overlapped by scrolling rows and no sticky is needed.
const HEADER_CELL = {
  bg: 'base-white',
  borderTop: '1px solid',
  borderBottom: '1px solid',
  borderColor: 'secondary-100',
  py: '10px',
};

// One above the bulk-queue popup this dialog is opened from (that popup sits
// above drawers/modals, so its own dialogs must sit above it).
const MODAL_Z_INDEX = BULK_OPERATION_QUEUE_Z_INDEX + 1;

export type SelectedCandidatesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  activity: UnderwayActivity;
};

const ValuePill = ({ children }: { children: React.ReactNode }) => (
  <Box
    as="span"
    display="inline-flex"
    alignItems="center"
    bg="secondary-50"
    color="secondary-700"
    borderRadius="6px"
    px="8px"
    py="3px"
    whiteSpace="nowrap"
    lineHeight="1.4"
  >
    <KText size="sm-regular" color="secondary-700">
      {children}
    </KText>
  </Box>
);

export const SelectedCandidatesModal = ({
  isOpen,
  onClose,
  activity,
}: SelectedCandidatesModalProps) => {
  const headerText = STR.header(activity.activityLabel, activity.stageName);
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="3xl">
      <ModalOverlay zIndex={MODAL_Z_INDEX} />
      <ModalContent
        containerProps={{ zIndex: MODAL_Z_INDEX }}
        borderRadius="6px"
        maxH="80vh"
        data-test-id="automation-selected-candidates-modal"
      >
        {/* Reserve space on the right (pr) so a long header truncates before
            the close button instead of running under it. */}
        <ModalHeader pl="12px" pr="48px" pb="0">
          <Flex alignItems="center" gap="10px" minW={0}>
            <Box
              bg="secondary-100"
              borderRadius="6px"
              px="8px"
              py="2px"
              flexShrink={0}
            >
              <KText size="sm-semi-bold" color="secondary-700">
                {activity.candidates.length}
              </KText>
            </Box>
            {/* Ellipsis + tooltip on overflow so a long header stays on one
                line and its full text is still readable on hover. */}
            <KText
              size="md-semi-bold"
              color="secondary-900"
              flex="1"
              minW={0}
              isTruncated
              enableTooltip
              tooltipText={headerText}
            >
              {headerText}
            </KText>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        {/* The modal body itself does NOT scroll — only the candidate list
            (tbody) below does — so the column header stays fixed above it. */}
        <ModalBody overflow="hidden" px="12px" pt="16px" pb="24px">
          <Table
            size="sm"
            variant="simple"
            sx={{
              // thead/tbody are block-level so the tbody can scroll on its own;
              // each row is a fixed table layout so the header and rows keep
              // matching (equal) column widths.
              '& tr': {
                display: 'table',
                width: '100%',
                tableLayout: 'fixed',
              },
            }}
          >
            <Thead display="block">
              <Tr>
                <Th {...HEADER_CELL} pl={0} pr="8px">
                  <KText
                    size="sm-semi-bold"
                    fontWeight="bold"
                    color="secondary-600"
                    textTransform="none"
                  >
                    {STR.columns.candidate}
                  </KText>
                </Th>
                <Th {...HEADER_CELL} px="8px">
                  <KText
                    size="sm-semi-bold"
                    fontWeight="bold"
                    color="secondary-600"
                    textTransform="none"
                  >
                    {STR.columns.job}
                  </KText>
                </Th>
                <Th {...HEADER_CELL} px="8px">
                  <KText
                    size="sm-semi-bold"
                    fontWeight="bold"
                    color="secondary-600"
                    textTransform="none"
                  >
                    {STR.columns.stage}
                  </KText>
                </Th>
              </Tr>
            </Thead>
            <Tbody
              display="block"
              maxH="55vh"
              overflowY="auto"
              // When this modal opens on top of an already-open drawer, the
              // drawer's react-remove-scroll lock (a bubble-phase document
              // wheel/touch listener) swallows scroll events over the modal.
              // Stop them bubbling to that listener so the list scrolls
              // natively; `overscrollBehavior: contain` keeps the scroll from
              // chaining to the page behind at the top/bottom edge.
              overscrollBehavior="contain"
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
              data-test-id="automation-selected-candidates-list"
            >
              {activity.candidates.map((c) => {
                const applicationUrl = getCandidateApplicationUrl(
                  activity.jobId,
                  c.id
                );
                return (
                  <Tr key={c.id}>
                    <Td
                      borderBottom="1px solid"
                      borderColor="secondary-100"
                      pl={0}
                      pr="8px"
                    >
                      <Flex alignItems="center" gap="10px">
                        <Avatar size="xs" name={c.name} src={c.avatarUrl} />
                        {applicationUrl ? (
                          <KText
                            as="a"
                            href={applicationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm-medium"
                            color="secondary-900"
                            cursor="pointer"
                            textDecoration="none"
                            _hover={{
                              color: 'primary-600',
                              textDecoration: 'underline',
                            }}
                          >
                            {c.name}
                          </KText>
                        ) : (
                          <KText size="sm-medium" color="secondary-900">
                            {c.name}
                          </KText>
                        )}
                      </Flex>
                    </Td>
                    <Td
                      borderBottom="1px solid"
                      borderColor="secondary-100"
                      px="8px"
                    >
                      <ValuePill>{activity.jobName}</ValuePill>
                    </Td>
                    <Td
                      borderBottom="1px solid"
                      borderColor="secondary-100"
                      px="8px"
                    >
                      <ValuePill>{activity.stageName}</ValuePill>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
